import type { CSSProperties, ForwardedRef, MouseEvent, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Button as MantineButton, LoaderProps } from '@mantine/core';
import clsx, { type ClassValue } from 'clsx';

import styles from './button.module.scss';

type Variants = 'primary' | 'secondary' | 'text';

export type ButtonProps = {
  /** Button label */
  children?: ReactNode;
  className?: string | ClassValue[];
  /** Disabled state */
  disabled?: boolean;
  /** increasing right / left padding */
  enlarge?: boolean;
  /** Sets button width to 100% of parent element */
  fullWidth?: boolean;
  /** Adds icon, e.g. <GoogleFontIcon>search</GoogleFontIcon>. Position of the icon is determined by iconPosition */
  icon?: ReactNode; // GoogleFontIcon | SVGIcon
  /** Determines if the icon is added left or right of the label. Defaults to 'left'  */
  iconPosition?: 'left' | 'right';
  /** Props spread to Loader component */
  loaderProps?: LoaderProps;
  /** Indicate loading state */
  loading?: boolean;
  /** Click handler */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Custom style */
  style?: CSSProperties;
  /** Order of focus when pressing the 'Tab' key on the keyboard in a form */
  tabIndex?: number;
  /** Button title attribute */
  title?: string;
  /** Button type attribute */
  type?: 'submit' | 'button' | 'reset';
  /** Set text-transform to uppercase */
  uppercase?: boolean;
  /** Controls button appearance */
  variant?: Variants;
};

/**
 * Standard themed Button component
 */
export const Button = forwardRef(
  (
    {
      className,
      children,
      enlarge,
      icon,
      iconPosition = 'left',
      variant = 'primary',
      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement | null>
  ) => {
    const iconAndText = children && (icon || props.loading);
    return (
      <MantineButton
        {...props}
        unstyled
        ref={ref}
        loaderProps={{
          size: '1em',
          color: 'rgb(var(--portal-ui-color-grey-3))',
        }}
        leftSection={iconPosition === 'left' ? icon : undefined}
        rightSection={iconPosition === 'right' ? icon : undefined}
        className={clsx(styles[`${variant}`], className)}
        classNames={{
          root: clsx(
            styles['root'],
            !children && styles['root--iconOnly'],
            enlarge && styles['root--enlarge']
          ),
          inner: clsx(
            styles['inner'],
            props.loading && styles['inner--loading'],
            iconAndText && styles['inner--iconAndText']
          ),
          loader: clsx(styles['loader'], {
            [styles['loader--hidden']]: props.loading !== true,
          }),
        }}
      >
        {children}
      </MantineButton>
    );
  }
);
